import {injectable} from 'inversify';
import {ListService} from "@/services/list-service";
import {ListBlackouter} from "@/utils/list-blackouter";
import ForumStore from "@/store/forum";
import {CommentsDto, ForumDto, ForumFilter, ForumTab, Tag, TopicDto} from "@/store/forum/types";
import {TopicPanel} from "@/scenes.ui.forum/topic-panel";
import {ListButtonPanel} from "@/scenes.ui/list-button-panel";
import ListenerManager from "@/managers/listener-manager";
import {EditShare} from "@/utils/edit-share";
import {PagingPanel} from "@/scenes.ui.forum/paging-panel";
import {FilterLine} from "@/scenes.ui.forum/filter-line";
import {FilterPanel} from "@/scenes.ui.forum/filter-panel";
import SoundUtils from "@/utils/sound-utils";
import {Keys} from "@/constants/key";
import {CommentPanel} from "@/scenes.ui.forum/comment-panel";
import {ListTextPanel} from "@/scenes.ui/list-text-panel";
import {ListSeparator} from "@/scenes.ui/list-separator";
import CU from "../utils/c-u";
import {TopicActionsPanel} from "@/scenes.ui.forum/topic-actions-panel";
import {ConfirmationPanel} from "@/scenes.ui/confirmation-panel";
import {ForumUtils} from "@/utils/forum-utils";
import {Tr} from "@/i18n/i18n";
import {MenuService} from "@/services/menu-service";
import PlayerStore from "@/store/player";
import {AccessLevel} from "@/store/player/types";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import BaseSound = Phaser.Sound.BaseSound;
import SpriteUtils from "@/utils/sprite-utils";

@injectable()
export class ForumService {

    private scene!: Scene;
    private canvas!: HTMLCanvasElement;
    private listService!: ListService;

    private selectSound!: BaseSound | undefined;
    private pagingPanel!: PagingPanel;
    private menuService!: MenuService | undefined;
    private menuContainer!: Container | undefined;

    private openProfile!: (playerId: number) => unknown;

    public openForum(scene: Scene, canvas: HTMLCanvasElement,
                     listService: ListService,
                     menuService: MenuService | undefined,
                     menuContainer: Container | undefined, clanId: number | undefined,
                     openProfile: (playerId: number) => unknown): void {

        this.menuService = menuService;
        this.menuContainer = menuContainer;
        let blackouter = this.init(scene, canvas, listService, menuService, menuContainer, clanId, openProfile);
        ForumStore.getDefault(clanId, 1).then((f: ForumDto) => {
            ForumStore.actualizeChosenTag(f);
            blackouter.notifyLoaded(() => {
                this.initTopic(f, blackouter, false, clanId);
            });
        });
    }

    public async openForumInstant(scene: Scene, canvas: HTMLCanvasElement,
                           listService: ListService,
                           menuService: MenuService | undefined,
                           menuContainer: Container | undefined, clanId: number | undefined,
                           openProfile: (playerId: number) => unknown): Promise<void> {

        return new Promise<void>(async (resolve, reject) => {
            this.menuService = menuService;
            this.menuContainer = menuContainer;
            let blackouter = this.init(scene, canvas, listService, menuService, menuContainer, clanId, openProfile);

            try {
                const f: ForumDto = await ForumStore.getDefault(clanId, 1);
                ForumStore.actualizeChosenTag(f);
                this.initTopic(f, blackouter, false, clanId);
                resolve();
            } catch (error) {
                console.error(error);
                reject(error);
            }
        });
    }

    public openTopic(scene: Scene, canvas: HTMLCanvasElement,
                     listService: ListService,
                     menuService: MenuService | undefined,
                     menuContainer: Container | undefined, clanId: number | undefined,
                     openProfile: (playerId: number) => unknown, topicId: number): void {
        let blackouter = this.init(scene, canvas, listService, menuService, menuContainer, clanId, openProfile)
        let self = this;

        ForumStore.getDefault(clanId, 1).then((f: ForumDto) => {
            ForumStore.getComments(topicId, 1).then((c: CommentsDto) => {
                blackouter.notifyLoaded(() => {
                    self.initComments(f, c, blackouter, false, clanId);
                    self.listService.setBackCallback(() => {
                        return self.openForumInstant(self.scene, self.canvas, self.listService, self.menuService, self.menuContainer, clanId, self.openProfile);
                    });
                });
            });
        });
    }

    public init(scene: Scene, canvas: HTMLCanvasElement,
                     listService: ListService,
                     menuService: MenuService | undefined,
                     menuContainer: Container | undefined, clanId: number | undefined,
                     openProfile: (playerId: number) => unknown): ListBlackouter {
        this.scene = scene;
        this.canvas = canvas;
        this.listService = listService;
        this.openProfile = openProfile;
        this.selectSound = SoundUtils.addSound(scene, Keys.SOUND_SELECT);

        let blackouter: ListBlackouter = listService.showSmart(scene, canvas, () => {
            if (menuService) {
                menuService.resourceToSide();
            }
            if (menuContainer) {
                menuContainer.setDepth(1000);
            }
        });
        if (menuService) {
            menuService.resourceToBottom();
        }
        if (menuContainer) {
            menuContainer.setDepth(4000);
        }

        return blackouter;
    }

    private initTopic(f: ForumDto, blackouter: ListBlackouter, re: boolean, clanId: number | undefined) {
        let self = this;
        let conts: Container[] = [];

        let fl: FilterLine = new FilterLine(this.scene, this.canvas, f.headTags, !!clanId, () => {
            let fp: FilterPanel = new FilterPanel(this.scene, this.canvas, this.selectSound, f, (tab: ForumTab, selectedTags: Tag[], filter: ForumFilter) => {
                fp.destroy();
                let pg = this.pagingPanel.getPage();
                ForumStore.getForum(clanId, tab, selectedTags, filter, 1).then((f: ForumDto) => {
                    self.initTopic(f, blackouter, true, clanId);
                });
            });
            self.scene.add.existing(fp);
        }, (tag:Tag|undefined) => {
            ForumStore.getForum(clanId, ForumTab.NEW, tag?[tag]:Object.values(Tag), ForumFilter.ALL_TIME, 1).then((f: ForumDto) => {
                ForumStore.chosenTag = tag;
                self.initTopic(f, blackouter, true, clanId);
            });
        });
        conts.push(fl);

        let al = PlayerStore.getPlayer().accessLevel;
        for (let t of f.topics) {
            let topicId = t.id;
            let tp: TopicPanel = new TopicPanel(this.scene, t, (vote: number) => {
                ForumStore.voteTopic(topicId, vote);
            }, (topicId:number) => {
                function initComments(page:number){
                    ForumStore.getComments(topicId, page || 1).then((c: CommentsDto) => {
                        self.initComments(f, c, blackouter, true, clanId);
                        self.listService.setBackCallback(() => {
                            return self.openForumInstant(self.scene, self.canvas, self.listService, self.menuService, self.menuContainer, clanId, self.openProfile);
                        });
                    });
                }
                let self = this;
                if(topicId !== t.id) {
                    ForumStore.getComments(topicId, 1).then((cc: CommentsDto) => {
                        initComments(cc.topic.commentsReadTillPage || 1);
                    });
                } else {
                    initComments(t.commentsReadTillPage || 1);
                }
            }, false, t.canEdit, () => {
                let actPanel: TopicActionsPanel = new TopicActionsPanel(self.scene, self.canvas, () => {
                    self.editTopic(f, t, blackouter, clanId);
                }, () => {
                    new ConfirmationPanel(this.scene!, this.canvas!, () => {
                        ForumStore.removeTopic(topicId).then((fd: ForumDto) => {
                            self.initTopic(fd, blackouter, true, clanId);
                        });
                    }, Tr('dlt'), Tr('dlt.conf'), Keys.UI_GOLDEN_CHECK, Tr('da'), Tr('net'), Keys.ATLAS_PROFILE).open();
                }, ()=>{
                    ForumStore.pinTopic(topicId, !t.pinned).then((fd: ForumDto) => {
                        self.initTopic(fd, blackouter, true, clanId);
                    });
                },(!clanId && (al === AccessLevel.ADMIN || al === AccessLevel.MODERATOR || al === AccessLevel.GOD)) ? t : undefined);
                actPanel.openAnimated();
            }, (playerId:number) => this.openProfile(playerId), true);
            conts.push(tp);
        }

        this.pagingPanel = new PagingPanel(this.scene, f.pageCurr, f.pageMax, (page: number) => {
            ForumStore.getDefault(clanId, page).then((f: ForumDto) => {
                self.initTopic(f, blackouter, true, clanId);
            });
        });
        conts.push(this.pagingPanel);

        if (f.canCreateTopic) {
            let createButt: Container = new ListButtonPanel(this.scene, Tr('tpc.crt'), 100, () => {
                CU.blockCanvas(self.scene!);
                EditShare.initPage(Tr('tpc.crtn'), true, true, '', '', [], false, f.canCreateGuestTopic,
                    (topicTitle: string, txt: string, tags: Tag[], guest: boolean, errorCallback: (err: string) => unknown) => {
                    //CanvasUtils.unblockCanvas();
                    ListenerManager.callEditPageCloseCallback();
                    ForumStore.createTopic(clanId, tags, topicTitle, txt, guest).then((f: ForumDto) => {
                        self.initTopic(f, blackouter, true, clanId);
                    });
                }, ForumUtils.getAvailableTags(clanId));
                ListenerManager.callEditPageOpenCallback();
            });
            conts.push(createButt);
        } else {
            if (clanId && clanId !== ForumUtils.COMMON_FORUM_ID) {
                let ltp = new ListTextPanel(this.scene, Tr('cln.frm.pr'), 100);
                conts.push(ltp);
            } else {
                let ltp = new ListTextPanel(this.scene, Tr('comm.frm.pr'), 100);
                conts.push(ltp);
            }
        }

        if (re) {
            blackouter.notifyReinitialized(conts, Tr('frm'));
        } else {
            blackouter.notifyInitialized(conts, Tr('frm'));
        }
    }

    private editTopic(f: ForumDto, t: TopicDto, blackouter: ListBlackouter, clanId: number | undefined) {
        let self = this;
        CU.blockCanvas(self.scene!);
        EditShare.initPage(Tr('tpc.edt'), true, true, t.title, t.text, t.tags, t.guest, f.canCreateGuestTopic,
            (topicTitle: string, txt: string, tags: Tag[], guest: boolean, errorCallback: (err: string) => unknown) => {
            ListenerManager.callEditPageCloseCallback();
            ForumStore.editTopic(t.id, tags, topicTitle, txt, guest).then((fd: ForumDto) => {
                self.initTopic(fd, blackouter, true, clanId);
            });
        }, ForumUtils.getAvailableTags(clanId));
        ListenerManager.callEditPageOpenCallback();
    }

    private initComments(f: ForumDto, c: CommentsDto, blackouter: ListBlackouter, re: boolean, clanId: number | undefined) {
        let self = this;
        let conts: Container[] = [];

        let tp: TopicPanel = new TopicPanel(this.scene, c.topic, (vote: number) => {
            ForumStore.voteTopic(c.topic.id, vote);
        }, (topicId:number) => {
            let self = this;
            function initComments(page:number){
                ForumStore.getComments(topicId, page).then((c: CommentsDto) => {
                    self.initComments(f, c, blackouter, true, clanId);
                    self.listService.setBackCallback(() => {
                        return self.openForumInstant(self.scene, self.canvas, self.listService, self.menuService, self.menuContainer, clanId, self.openProfile);
                    });
                });
            }
            if(topicId !== c.topic.id) {
                ForumStore.getComments(topicId, 1).then((cc: CommentsDto) => {
                    initComments(cc.topic.commentsReadTillPage || 1);
                });
            } else {
                initComments(c.topic.commentsReadTillPage || 1);
            }
        }, true, c.topic.canEdit, () => {
            self.editTopic(f, c.topic, blackouter, clanId);
        }, (playerId:number) => this.openProfile(playerId));
        conts.push(tp);

        const cmntsBack = SpriteUtils.addImage(this.scene, -CU.CANVAS_WIDTH/2 / CU.S, 0, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_PANEL_BACK).setOrigin(0, 0);
        SpriteUtils.setDispSize(cmntsBack, CU.CANVAS_WIDTH, 40 * CU.S);
        let cmntsBackCont = new Container(this.scene, 0, 0, [cmntsBack]);
        conts.push(cmntsBackCont);

        let ltp = new ListTextPanel(this.scene, Tr('cmnts'), 30);
        conts.push(ltp);

        let ls = new ListSeparator(this.scene);
        conts.push(ls);

        let replyCallback = (answerTo?:string) => {
            CU.blockCanvas(self.scene!);
            EditShare.initPage(Tr('com.crtn'), false, false, '', answerTo? answerTo + ", " : "", [], false, false,
                (topicTitle: string, txt: string, tags: Tag[], guest: boolean, errorCallback: (err: string) => unknown) => {
                    //CanvasUtils.unblockCanvas();
                    ListenerManager.callEditPageCloseCallback();
                    ForumStore.createComment(c.topic.id, txt).then((cd: CommentsDto) => {
                        self.initComments(f, cd, blackouter, true, clanId);
                        self.listService.setBackCallback(() => {
                            return self.openForumInstant(self.scene, self.canvas, self.listService, self.menuService, self.menuContainer, clanId, self.openProfile);
                        });
                    });
                }, []);
            ListenerManager.callEditPageOpenCallback();
        }

        for (let t of c.comments) {
            let commId = t.id;
            let tp: CommentPanel = new CommentPanel(this.scene, t, (vote: number) => {
                ForumStore.voteComment(t.id, vote);
            }, t.canEdit, () => {
                let actPanel: TopicActionsPanel = new TopicActionsPanel(self.scene, self.canvas, () => {
                    let self = this;
                    CU.blockCanvas(this.scene!);
                    EditShare.initPage(Tr('com.edt'), false, false, '', t.text, [], false, false,
                        (topicTitle: string, txt: string, tags: Tag[], guest: boolean, errorCallback: (err: string) => unknown) => {
                            ListenerManager.callEditPageCloseCallback();
                            ForumStore.editComment(t.id, txt).then((cd: CommentsDto) => {
                                self.initComments(f, cd, blackouter, true, clanId);
                                self.listService.setBackCallback(() => {
                                    return self.openForumInstant(self.scene, self.canvas, self.listService, self.menuService, self.menuContainer, clanId, self.openProfile);
                                });
                            });
                        }, []);
                    ListenerManager.callEditPageOpenCallback();
                }, () => {
                    new ConfirmationPanel(this.scene!, this.canvas!, () => {
                        ForumStore.removeComment(commId).then((cb: CommentsDto) => {
                            self.initComments(f, cb, blackouter, true, clanId);
                            self.listService.setBackCallback(() => {
                                return self.openForumInstant(self.scene, self.canvas, self.listService, self.menuService, self.menuContainer, clanId, self.openProfile);
                            });
                        });
                    }, Tr('dlt'), Tr('com.dlt.conf'), Keys.UI_GOLDEN_CHECK, Tr('da'), Tr('net'), Keys.ATLAS_PROFILE).open();
                }, undefined, undefined);
                actPanel.openAnimated();
            }, () => this.openProfile(t.playerId), replyCallback);
            conts.push(tp);
        }

        if (c.canCreateComment) {
            let createButt: Container = new ListButtonPanel(this.scene, Tr('write'), 100, replyCallback);
            conts.push(createButt);
        }

        this.pagingPanel = new PagingPanel(this.scene, c.pageCurr, c.pageMax, (page: number) => {
            ForumStore.getComments(c.topic.id, page).then((cd: CommentsDto) => {
                self.initComments(f, cd, blackouter, true, clanId);
                self.listService.setBackCallback(() => {
                    return self.openForumInstant(self.scene, self.canvas, self.listService, self.menuService, self.menuContainer, clanId, self.openProfile);
                });
            });
        });

        conts.push(this.pagingPanel);

        if (re) {
            blackouter.notifyReinitialized(conts, Tr('discn'));
        } else {
            blackouter.notifyInitialized(conts, Tr('discn'));
        }
    }
}
